import { useEffect, useMemo, useState } from 'react';

import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '../../components/common/Button';
import { Card, CardContent, CardHeader } from '../../components/common/Card';
import { Input } from '../../components/forms/Input';
import { MarketingSiteHeader } from '../../components/layout/MarketingSiteHeader';
import { ROUTES } from '../../constants';
import {
  submitContactInquiry,
  type ContactTopic,
} from '../../services/marketingContactService';
import { useAppSelector } from '../../store/hooks';
import { HOME_PAGE_TITLE } from '../home/homeMarketing';

const TOPIC_COPY: Record<
  ContactTopic,
  { title: string; intro: string; defaultSubject: string }
> = {
  demo: {
    title: 'Schedule a guided demo',
    intro:
      'Tell us about your institution and what you want to see. We will follow up by email to arrange a walkthrough.',
    defaultSubject: 'Guided demo request',
  },
  general: {
    title: 'Contact Strat-Edu',
    intro:
      'Questions about the platform, partnerships, or how Strat-Edu fits your administrative team? Send a note below.',
    defaultSubject: 'Website inquiry',
  },
  support: {
    title: 'Product support',
    intro:
      'Existing customers: describe what you need help with and we will route your message to the right person.',
    defaultSubject: 'Support request',
  },
};

function parseTopic(value: string | null): ContactTopic {
  if (value === 'demo' || value === 'support') {
    return value;
  }
  return 'general';
}

export function ContactPage() {
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const [searchParams] = useSearchParams();
  const topic = parseTopic(searchParams.get('topic'));
  const copy = TOPIC_COPY[topic];

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = `Contact — ${HOME_PAGE_TITLE.replace(/^Strat-Edu — /, '')}`;
    setSubject(copy.defaultSubject);
  }, [copy.defaultSubject]);

  const messagePlaceholder = useMemo(() => {
    if (topic === 'demo') {
      return 'Who should join the demo, which modules matter most, and any timing preferences?';
    }
    return 'How can we help?';
  }, [topic]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await submitContactInquiry({
        name,
        email,
        organization: organization.trim() || undefined,
        subject: subject.trim() || copy.defaultSubject,
        message,
        topic,
        website: website.trim() || undefined,
      });
      toast.success('Message sent. We will reply by email soon.');
      setMessage('');
      setWebsite('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--se-porcelain)] text-[var(--se-ink)]">
      <MarketingSiteHeader isAuthenticated={isAuthenticated} />
      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <p className="text-sm text-[var(--se-ink-muted)]">
          <Link to={ROUTES.HOME} className="font-medium text-[var(--se-route-teal)]">
            ← Back to home
          </Link>
        </p>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight">{copy.title}</h1>
        <p className="mt-3 text-base leading-relaxed text-[var(--se-ink-muted)]">
          {copy.intro}
        </p>
        <Card className="mt-8 border-[var(--se-rule)] shadow-sm">
          <CardHeader>
            <h2 className="text-lg font-semibold">Send a message</h2>
            <p className="mt-1 text-sm text-[var(--se-ink-muted)]">
              We route submissions to our team and reply to the email you provide.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="absolute left-[-9999px]" aria-hidden>
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                />
              </div>
              <Input
                label="Your name"
                name="name"
                required
                autoComplete="name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
              <Input
                label="Work email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <Input
                label="Organization (optional)"
                name="organization"
                autoComplete="organization"
                value={organization}
                onChange={e => setOrganization(e.target.value)}
              />
              <Input
                label="Subject"
                name="subject"
                required
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
              <div>
                <label
                  htmlFor="contact-message"
                  className="mb-1 block text-sm font-medium text-[var(--se-ink)]"
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={6}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={messagePlaceholder}
                  className="w-full rounded-md border border-[var(--se-rule)] bg-white px-3 py-2 text-sm text-[var(--se-ink)] shadow-sm focus:border-[var(--se-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--se-focus)]/25"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? 'Sending…' : 'Send message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
